import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from '@/lib/api/types';
import { useAuth } from './auth-context';
import { hasRole } from './roles';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Minimum role required. Omit to allow any authenticated user. */
  requireRole?: Role;
  /** Where to send unauthorized (authenticated but under-privileged) users. */
  forbiddenRedirect?: string;
}

/**
 * Gates a route on authentication and, optionally, a minimum role. While auth
 * is resolving it renders nothing (the shell shows its own skeleton); the brief
 * window avoids a redirect flash.
 */
export function ProtectedRoute({
  children,
  requireRole,
  forbiddenRedirect = '/app',
}: ProtectedRouteProps): JSX.Element | null {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return null;

  if (status === 'unauthenticated' || !user) {
    // O destino inteiro, com query e âncora: quem veio pelo "código de indicação"
    // (/assinatura/checkout#indicacao) volta ao campo, não só à página.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  if (requireRole && !hasRole(user.role, requireRole)) {
    return <Navigate to={forbiddenRedirect} replace />;
  }

  return <>{children}</>;
}
